import React from 'react';
import { Building2, Users, Plane, Plus, Check, MessageSquare, X, Loader2, AlertTriangle, RotateCcw } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card/Card';
import { Badge } from '@/components/ui/Badge/Badge';
import { Button } from '@/components/ui/Button/Button';
import { VStack, HStack } from '@/components/ui/Stack';
import { PropertyDetail, PropertyDetails } from '@/components/ui/PropertyDetail';
import { MatchScore } from '@/components/ui/MatchScore';
import { SimpleTooltip } from '@/components/ui/Tooltip';
import { AIIcon } from '@/components/ui';
import type { VenueDetails } from '@/services/ResearchService';
import { StarRating } from '@/components/ui/StarRating';

interface VenueCardProps {
  venue: VenueDetails;
  isShortlisted?: boolean;
  onAddToShortlist?: () => void;
  onRemoveFromShortlist?: () => void;
  onViewDetails?: () => void;
  onNotesClick?: () => void;
  showNotes?: boolean;
  hasNotes?: boolean;
  isAddingToShortlist?: boolean;
  showAIScores?: boolean; // Whether to display AI match scores and recommendations
  // AI Research controls
  onRunResearch?: () => void;
  onResetResearch?: () => void;
  isEnriching?: boolean;
  canRunResearch?: boolean;
}

export function VenueCard({
  venue,
  isShortlisted = false,
  onAddToShortlist,
  onRemoveFromShortlist,
  onViewDetails,
  onNotesClick,
  showNotes = false,
  hasNotes = false,
  isAddingToShortlist = false,
  showAIScores = true,
  onRunResearch,
  onResetResearch,
  isEnriching = false,
  canRunResearch = false,
}: VenueCardProps) {
  // Calculate enrichment status
  const TIMING = { TIMEOUTS: { JOB_STUCK: 5 * 60 * 1000 } }; // 5 minutes
  const enrichmentDate = venue.enrichment_completed_at 
    ? new Date(venue.enrichment_completed_at).toLocaleString()
    : null;
  
  const jobStatus = venue.enrichment_job_status;
  const isQueued = jobStatus === 'queued' || isEnriching;
  const isProcessing = jobStatus === 'processing';
  const hasActiveJob = isQueued || isProcessing;
  
  // Calculate actual processing duration (only if job has started)
  const processingDuration = venue.enrichment_job_started_at 
    ? Date.now() - new Date(venue.enrichment_job_started_at).getTime()
    : 0;
  
  // A job is only "stuck" if it's actually processing and has been for > 5 minutes
  const isStuck = isProcessing && processingDuration > TIMING.TIMEOUTS.JOB_STUCK;
  

  return (
    <Card padding="none" className="hover:shadow-md transition-shadow p-3">
      <CardContent className="p-0">
        <VStack gap={1} className="gap-1.5">
          {/* Header: Name, Location, Star Rating */}
          <VStack gap={0} className="gap-0.5">
            <div className="flex items-start gap-2">
              <h3 
                className="font-semibold text-sm truncate cursor-pointer hover:text-primary-600 transition-colors flex-1"
                onClick={onViewDetails}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onViewDetails?.();
                  }
                }}
              >
                {venue.property_name}
              </h3>
              {venue.star_rating && (
                <div className="flex-shrink-0">
                  <StarRating rating={venue.star_rating} size="xs" showEmptyStars={true} />
                </div>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              {venue.city}
              {venue.country && `, ${venue.country}`}
            </p>
          </VStack>

          {/* Property Details */}
          <PropertyDetails className="gap-3">
            {venue.number_of_rooms && (
              <PropertyDetail
                icon={<Building2 />}
                value={venue.number_of_rooms}
                label="rooms"
                size="sm"
              />
            )}
            {venue.maximum_capacity && (
              <PropertyDetail
                icon={<Users />}
                value={venue.maximum_capacity}
                label="capacity"
                size="sm"
              />
            )}
            {venue.nearest_airport_distance_km !== null && venue.nearest_airport_distance_km !== undefined && (
              <PropertyDetail
                icon={<Plane />}
                value={`${Number(venue.nearest_airport_distance_km).toFixed(1)}km`}
                size="sm"
              />
            )}
          </PropertyDetails>

          {/* AI Match Score (if available and should be shown) */}
          {showAIScores && venue.match_score !== null && venue.match_score !== undefined && (
            <MatchScore
              score={venue.match_score}
              reasoning={venue.match_reasoning ?? undefined}
              recommendation={venue.recommendation ?? undefined}
              className="mt-1"
            />
          )}

          {/* Actions */}
          <HStack gap={1} align="center" justify="between" className="mt-4">
            <HStack gap={1} align="center">
              {isShortlisted ? (
                <>
                  <Badge variant="success" className="flex-shrink-0 text-xs px-2 py-0.5 h-6">
                    <Check className="w-3 h-3 mr-1" />
                    Shortlisted
                  </Badge>
                  {showNotes && onNotesClick && (
                    <div className="relative">
                      <Button
                        size="sm"
                        variant="ghost"
                        leftIcon={<MessageSquare className="w-3.5 h-3.5" />}
                        onClick={onNotesClick}
                        className="h-7 px-2 text-xs"
                      >
                        Notes
                      </Button>
                      {hasNotes && (
                        <div 
                          className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-success-500 rounded-full ring-2 ring-white"
                          aria-label="Has notes"
                        />
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  {onAddToShortlist && (
                    <Button
                      size="sm"
                      variant="secondary"
                      leftIcon={<Plus className="w-3.5 h-3.5" />}
                      onClick={onAddToShortlist}
                      disabled={isAddingToShortlist}
                      isLoading={isAddingToShortlist}
                      className="h-7 text-xs"
                    >
                      {isAddingToShortlist ? 'Adding...' : 'Add to Shortlist'}
                    </Button>
                  )}
                </>
              )}
            </HStack>
            <HStack gap={1} align="center" justify="end">
              {/* Research Status and Actions - Right Side */}
              {canRunResearch && (
                <HStack gap={1} align="center" className="gap-1.5">
                  {hasActiveJob ? (
                    <>
                      {isStuck && <AlertTriangle className="w-3.5 h-3.5 text-orange-500" />}
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-primary-600" />
                      <span className={`text-xs whitespace-nowrap ${isStuck ? 'text-orange-500' : isProcessing ? 'text-warning-600' : 'text-muted-foreground'}`}>
                        {isStuck ? 'Stuck' : isProcessing ? 'Processing' : 'Queued'}
                      </span>
                      {isStuck && onResetResearch && (
                        <SimpleTooltip content="Reset Research" side="top">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={onResetResearch}
                            className="h-7 w-7 p-0"
                          >
                            <RotateCcw className="w-3.5 h-3.5 text-orange-500" />
                          </Button>
                        </SimpleTooltip>
                      )}
                    </>
                  ) : (
                    <>
                      {onRunResearch && (
                        <SimpleTooltip content="Run Venue Research" side="top">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={onRunResearch}
                            className="h-7 w-7 p-0 hover:bg-accent-50"
                          >
                            <AIIcon className="w-3.5 h-3.5" />
                          </Button>
                        </SimpleTooltip>
                      )}
                      <SimpleTooltip
                        content={
                          enrichmentDate 
                            ? `Last researched: ${enrichmentDate}${venue.enrichment_source ? ` (${venue.enrichment_source})` : ''}`
                            : 'Not yet researched'
                        }
                        side="top"
                      >
                        <span 
                          className={`text-xs whitespace-nowrap ${
                            venue.enrichment_status === 'enriched' ? 'text-success-600' : 
                            venue.enrichment_status === 'failed' ? 'text-error-600' : 'text-muted-foreground'
                          }`}
                        >
                          {venue.enrichment_status === 'enriched' ? '✓ researched' : venue.enrichment_status}
                        </span>
                      </SimpleTooltip>
                    </>
                  )}
                </HStack>
              )}
              {isShortlisted && onRemoveFromShortlist && (
                <button
                  onClick={onRemoveFromShortlist}
                  className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                  aria-label="Remove from shortlist"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </HStack>
          </HStack>
        </VStack>
      </CardContent>
    </Card>
  );
}

